import type { Locale } from './brand';

/**
 * Single source of truth for all page URL paths per locale.
 *
 * Used by: navigation, hreflang tags, sitemap, language switcher.
 *
 * Convention: Spanish pages live at the root (no prefix), English pages
 * under `/en/`. Page slugs are localised for SEO (e.g. `/privacidad`
 * vs `/en/privacy`).
 */
export const pageRoutes = {
  home: { es: '/', en: '/en' },
  faq: { es: '/faq', en: '/en/faq' },
  privacy: { es: '/privacidad', en: '/en/privacy' },
  cookies: { es: '/cookies', en: '/en/cookies' },
  legal: { es: '/aviso-legal', en: '/en/legal' },
  bonos: { es: '/bonos', en: '/en/bonos' },
} as const;

export type PageId = keyof typeof pageRoutes;

/** Get the path for a page in a given locale. */
export function pagePath(page: PageId, locale: Locale): string {
  return pageRoutes[page][locale];
}

/**
 * Given the current pathname, determine which page we're on and return the
 * equivalent path in the target locale. Falls back to home if unrecognised.
 */
export function switchLocalePath(pathname: string, targetLocale: Locale): string {
  for (const [pageId, paths] of Object.entries(pageRoutes)) {
    const esPath = paths.es;
    const enPath = paths.en;
    if (pathname === esPath || pathname === enPath) {
      return pageRoutes[pageId as PageId][targetLocale];
    }
  }
  return pageRoutes.home[targetLocale];
}

/** Navigation links shown in the main nav and used for SiteNavigationElement schema. */
export const navLinks = ['home'] as const satisfies readonly PageId[];

/** Footer legal links. */
export const legalLinks = ['privacy', 'cookies', 'legal'] as const satisfies readonly PageId[];
